import React, { useEffect, useState } from "react";
import { Drawer, Button, Text, Loader, Group } from "@mantine/core";
import {
  assignScopeToMultipleOrgOrProduct,
  getDistributorOrgs,
  getOrgsOfDistributor,
} from "../../utils/api-communication/RestApiCli";
import OrganizationsTable from "./OrganizationsTable";
import { Organization } from "types/accountManagementTypes";

interface Distributor {
  orgId: string;
  name: string;
  email: string;
  address: string;
  country?: string;
  creationDate: string;
  distId?: string;
  products: { serialNo: string }[];
}

interface DistributorOrganizationsDrawerProps {
  opened: boolean;
  onClose: () => void;
  scopeName: string;
}

const DistributorOrganizationsDrawer: React.FC<DistributorOrganizationsDrawerProps> = ({
  opened,
  onClose,
  scopeName,
}) => {
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [selectedDist, setSelectedDist] = useState<Distributor | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [selectedDistOrgs, setSelectedDistOrgs] = useState<Organization[]>([]);

  useEffect(() => {
    if (opened) {
      setLoading(true);
      getDistributorOrgs(
        (res) => {
          // Add oculera as a distributor at the top
          const oculeraDistributor: Distributor = {
            name: "Oculera",
            distId: "oculera",
            address: "",
            creationDate: "",
            products: [],
            orgId: "oculera",
            email: "",
          };
          setDistributors([oculeraDistributor, ...res.data]);
          setLoading(false);
        },
        (_err) => {
          setLoading(false);
        },
      );
      setSelectedDist(null);
    }
  }, [opened]);

  useEffect(() => {
    if (selectedDist) {
      setLoading(true);
      getOrgsOfDistributor(
        { distId: selectedDist.distId || selectedDist.orgId },
        (res) => {
          setSelectedDistOrgs(res.data);
          setLoading(false);
        },
        (_err) => {
          setSelectedDistOrgs([]);
          setLoading(false);
        },
      );
      setSelectedOrgs([]);
    }
  }, [selectedDist]);

  const handleAssignScope = async () => {
    if (!selectedDist || selectedOrgs.length === 0) return;
    if (!scopeName) {
      console.error("No scope selected");
      return;
    }

    assignScopeToMultipleOrgOrProduct(
      {
        orgIds: selectedOrgs,
        scopeName,
      },
      () => {
        console.log("Scope assigned successfully");
        setSelectedOrgs([]);
        onClose();
      },
      (error) => {
        console.error("Failed to assign scope:", error);
      },
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={selectedDist ? "Organizations" : "Distributors"}
      size="xxl"
    >
      {loading ? (
        <Loader />
      ) : selectedDist ? (
        <>
          <Group mb="md">
            <Button size="xs" onClick={() => setSelectedDist(null)}>
              Back to Distributors
            </Button>
            <Text fw={700}>{selectedDist.name}</Text>
          </Group>
          {selectedOrgs.length > 0 && (
            <Group mb="sm">
              <Text fw={500}>Selected Organizations:</Text>
              {selectedDistOrgs
                .filter((org: any) => selectedOrgs.includes(org.orgId))
                .map((org: any) => (
                  <Text key={org.orgId} c="teal" size="sm">
                    {org.name}
                  </Text>
                ))}
            </Group>
          )}
          <Group mt="md" justify="flex-end">
            <Button onClick={handleAssignScope} disabled={selectedOrgs.length === 0}>
              Assign
            </Button>
          </Group>
          <OrganizationsTable
            organizations={selectedDistOrgs as any}
            showDetails={true}
            showCountry={true}
            showDistId={false}
            dateLocale="en-GB"
            multiSelect={true}
            selected={selectedOrgs}
            onSelectChange={setSelectedOrgs}
            showScopeName={true}
          />
        </>
      ) : (
        <OrganizationsTable
          organizations={distributors as any}
          showDetails={false}
          showCountry={true}
          showDistId={true}
          dateLocale="en-GB"
          onDistSelect={(distId) => {
            const dist = distributors.find((d) => d.distId === distId || d.orgId === distId);
            if (dist) setSelectedDist(dist);
          }}
          showScopeName={false}
        />
      )}
    </Drawer>
  );
};

export default DistributorOrganizationsDrawer;
